import React, { useState } from 'react';

import { Box, Icon, Sheet, Text, VisuallyHidden } from '@betterment/samba-react';
import { cva } from '@betterment/samba-styled-system/css';
import { toTitleCase, useFeatureFlag } from '@betterment/shared-components';

import { ClientName } from './ClientName';
import Column from './Column';

import { ActivityItemFragment } from '../../../graphql/fragments/activityItem.generated';
import { ActivityItemStage } from '../../../graphql/schema';
import { extractActivityData } from '../utils/Extractors';

import { Drawer } from '.';

interface RowProps {
  item: ActivityItemFragment;
}

const activityStatusStyles = cva({
  variants: {
    status: {
      [ActivityItemStage.Initiated]: { color: 'text.weak' },
      [ActivityItemStage.Failed]: { color: 'text.critical' },
      [ActivityItemStage.Sent]: { color: 'text.weak' },
      [ActivityItemStage.Returned]: { color: 'text.warning' },
      [ActivityItemStage.Completed]: { color: 'text.success' },
      [ActivityItemStage.Canceled]: { color: 'text.critical' },
    },
  },
});

const EnvelopeAccountNameRow: React.FC<{
  envelopeName?: string;
  subAccountName?: string;
}> = ({ envelopeName, subAccountName }) => {
  const hasEnvelope = Boolean(envelopeName);

  return (
    <>
      <Text>{envelopeName || subAccountName}</Text>
      {hasEnvelope && (
        <Text variant="text50" color="text.weak">
          {subAccountName}
        </Text>
      )}
    </>
  );
};

export const Row: React.FC<RowProps> = ({ item }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { enabled: isEnvelopesEnabled, loading } = useFeatureFlag({
    splitName: 'retail.b4a_ax_q2_2025_envelopes_enabled',
    context: 'Activity',
  });

  const { time, date, envelopeName, accountName, description, memo, activityValue, status, users, subAccountName } =
    extractActivityData(item);

  const shouldShowStatusStage = status && ![ActivityItemStage.Sent, ActivityItemStage.Completed].includes(status);

  if (loading) {
    return null;
  }

  return (
    <Sheet overlay="weak" open={isOpen} onOpenChange={setIsOpen}>
      <Sheet.Trigger asChild={true}>
        <Box
          data-testid="activity-row"
          css={{
            px: 'space.300',
            py: 'space.200',
            display: 'grid',
            gap: 'space.200',
            gridTemplateAreas: `"timestamp clientName goal description value"`,
            gridTemplateColumns: '1fr 1.5fr 2fr 2fr 1.5fr',
            alignItems: 'center',
            backgroundColor: isOpen ? 'background.info' : undefined,
          }}
        >
          <Box
            css={{
              gridArea: 'timestamp',
            }}
          >
            <Column>
              <Text>{date}</Text>
              <Text variant="text50" color="text.weak">
                {time}
              </Text>
            </Column>
          </Box>
          <Box css={{ gridArea: 'clientName' }}>
            <Column>
              <ClientName users={users} />
            </Column>
          </Box>
          <Box
            css={{
              gridArea: 'goal',
            }}
          >
            <Column>
              {isEnvelopesEnabled ? (
                <EnvelopeAccountNameRow envelopeName={envelopeName} subAccountName={subAccountName} />
              ) : (
                <Text>{accountName}</Text>
              )}
            </Column>
          </Box>
          <Box css={{ gridArea: 'description' }}>
            <Column>
              <Text>{description}</Text>
              {memo && <Text color="text.weak">{memo}</Text>}
            </Column>
          </Box>
          <Box
            css={{
              gridArea: 'value',
            }}
          >
            <Box
              css={{
                textAlign: 'right',
                justifyContent: 'end',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 'space.200',
              }}
            >
              <Column>
                <Text>{activityValue}</Text>
                {shouldShowStatusStage && (
                  <Text variant="text50" className={activityStatusStyles({ status })}>
                    {status === ActivityItemStage.Initiated ? 'Pending' : toTitleCase(status)}
                  </Text>
                )}
              </Column>
              <Icon size="small" name="CaretRight" />
            </Box>
          </Box>
        </Box>
      </Sheet.Trigger>
      <Sheet.Content css={{ overflowY: 'auto', gap: 'space.300' }} onOpenAutoFocus={e => e.preventDefault()}>
        <VisuallyHidden asChild={true}>
          <Sheet.Description>{description}</Sheet.Description>
        </VisuallyHidden>
        <Drawer item={item} />
      </Sheet.Content>
    </Sheet>
  );
};
